import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {useAppSelector} from '@/hooks';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {useTimelineWidthPerSecond} from '../../hooks/timeline-hooks';
import {pixelsToTime, timeToPixels} from '../../utils/timeline-calculations';
import {RULER_HEIGHT, TIMELINE_HORIZONTAL_PADDING, TIMELINE_RIGHT_MARGIN} from '../../timeline.types';
import {formatSecondsToTimeString} from '@Utils/date.util';
import styles from './timeline-ruler.module.scss';
import {useTimelineScrollContainerRef} from '@Components/poster-editor/components/timeline/hooks/useTimelineRefs';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';

const getStepSizeFromWidthPerSecond = (widthPerSecond: number): number => {
  if (widthPerSecond >= 250) {
    return 0.5;
  }
  if (widthPerSecond >= 80) {
    return 1;
  }
  if (widthPerSecond >= 70) {
    return 2;
  }
  if (widthPerSecond >= 60) {
    return 3;
  }
  if (widthPerSecond >= 45) {
    return 5;
  }
  if (widthPerSecond >= 30) {
    return 10;
  }
  if (widthPerSecond >= 15) {
    return 15;
  }
  if (widthPerSecond >= 5) {
    return 30;
  }
  return 60;
};

const getStepSizeForDots = (widthPerSecond: number): number => {
  if (widthPerSecond > 40) {
    return 1;
  }
  if (widthPerSecond < 20) {
    return 15;
  }
  return 5;
};

export function TimelineRuler(): ReactElement {
  const pageDuration = useAppSelector(getCurrentPageDuration) ?? 0;
  const pixelsPerSecond = useTimelineWidthPerSecond();
  const containerRef = useTimelineScrollContainerRef();
  const [containerClientWidth, setContainerClientWidth] = useState(containerRef.current?.clientWidth ?? 0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setContainerClientWidth(el.clientWidth);
    });
    observer.observe(el);
    return (): void => {
      observer.disconnect();
    };
  }, []);

  const containerWidth = containerClientWidth - TIMELINE_HORIZONTAL_PADDING * 2;
  const containerWidthInDuration = containerWidth / pixelsPerSecond;
  const totalDuration = Math.max(containerWidthInDuration, pageDuration + pixelsToTime(TIMELINE_RIGHT_MARGIN, pixelsPerSecond));

  const rulerWidth = timeToPixels(totalDuration, pixelsPerSecond);
  const stepSize = getStepSizeFromWidthPerSecond(pixelsPerSecond);
  const stepSizeForDots = Math.round(getStepSizeForDots(pixelsPerSecond));

  const prepareTimestamps = (): ReactElement[] => {
    const elements: ReactElement[] = [];

    elements.push(
      <span className={styles.zero} key="zero">
        0s
      </span>
    );

    const maxTime = Math.ceil(totalDuration);

    if (stepSize === 0.5) {
      for (let i = 0.5; i < maxTime; i = Math.round((i + 0.5) * 100) / 100) {
        const dotPosition = timeToPixels(i - 0.25, pixelsPerSecond);
        elements.push(<div key={`dot-${i - 0.25}`} className={styles.dotTimestamp} style={{left: `${dotPosition}px`}} />);

        const position = timeToPixels(i, pixelsPerSecond);
        const formattedValue = formatSecondsToTimeString(i, {showDecimals: true});
        elements.push(
          <span key={`timestamp-${i}`} className={`${styles.timestamp} ${formattedValue.length <= 2 ? styles.smallOffset : styles.largeOffset}`} style={{left: `${position}px`}}>
            {formattedValue}
          </span>
        );
      }
      return elements;
    }

    for (let i = 1; i < maxTime; i += 1) {
      const position = timeToPixels(i, pixelsPerSecond);

      if (i % stepSize === 0) {
        const formattedValue = formatSecondsToTimeString(i);
        elements.push(
          <span key={`timestamp-${i}`} className={`${styles.timestamp} ${formattedValue.length <= 2 ? styles.smallOffset : styles.largeOffset}`} style={{left: `${position}px`}}>
            {formattedValue}
          </span>
        );
      } else if (stepSizeForDots === 1 || i % stepSizeForDots === 0) {
        elements.push(<div key={`dot-${i}`} className={styles.dotTimestamp} style={{left: `${position}px`}} />);
      }
    }

    return elements;
  };

  return (
    <div
      className={`${styles.ruler} ${isEditorMobileVariant() ? styles.mobile : ''}`}
      style={{
        width: `${rulerWidth}px`,
        height: `${RULER_HEIGHT}px`,
      }}
    >
      {prepareTimestamps()}
    </div>
  );
}
